/**
 * The sentence a destructive client act has to answer for.
 *
 * MOVED here from `pages/client-roster/model/confirmPrompt.ts` on 16 September 2026, with
 * `clientActionOutcome`, and for the same reason: the client's own card can now end a coaching
 * relationship, so two surfaces raise this dialog.
 *
 * **It takes a NAME, not a row.** That was the only thing it ever used a `ClientRow` for, and
 * taking the string is what let it move: the client detail has a name and no row.
 *
 * `ConfirmDialog` words nothing — every string arrives on the prompt, already resolved by the model
 * that knows what is about to happen. This is that model.
 *
 * Both prompts name a CONSEQUENCE the trainer cannot see from the surface they are on. Ending a
 * coaching relationship frees the seat immediately but locks the pair for 48 hours
 * (`Domain/CoachingRotationLockout`), so a trainer rotating a client back in the same afternoon
 * would find the invite refused with no warning. Cancelling an invitation frees its seat too, which
 * is the only reason a trainer would reach for it at the cap.
 *
 * `t` arrives as a parameter — this is `model/`, and it imports no i18next.
 */

import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

import type { RowAction } from './clientActionOutcome';

type Translate = (key: string, values?: Record<string, unknown>) => string;

/**
 * @param consequence.removesEntry cancelling this invitation also deletes a roster ENTRY — see
 *   `withdrawRemovesEntry`, which is where the roster reads it from. Its plan, surveys and schedule
 *   go with it, and the confirm says so in the mobile app's own words. An offer deletes nothing but
 *   itself, and the plain sentence is right for it. Ignored for ending a coaching relationship.
 */
export function clientConfirmPrompt(
  action: Exclude<RowAction, 'resend'>,
  name: string,
  t: Translate,
  busy: boolean,
  consequence?: { readonly removesEntry: boolean },
): ConfirmPrompt {
  const namespace = action === 'endCoaching' ? 'web:clients.endCoaching' : 'web:clients.cancelInvite';
  const description =
    action === 'cancelInvite' && consequence?.removesEntry === true
      ? t('coaching.withdraw.entryWarning')
      : t(`${namespace}.body`);

  return {
    // The name is NOT interpolated into the question. Polish would decline it — "z Kubą
    // Zielińskim", "dla Kuby Zielińskiego", two different cases — and the app cannot inflect a
    // name and must not guess. It goes on `subject`, where the nominative is correct.
    title: t(`${namespace}.title`),
    subject: name,
    description,
    confirmLabel: t(`${namespace}.confirm`),
    cancelLabel: t(`${namespace}.cancel`),
    // Both of these destroy a relationship the trainer cannot rebuild from this screen, so the
    // confirm is drawn in `--danger` and Cancel stays neutral — see `ConfirmDialog`'s own note.
    destructiveConfirm: true,
    busy,
  };
}
